import React, { useEffect, useRef, useState } from 'react'
import { Modal, CarpartsPlusGoldLogoModal } from '@usap/uicomponents'
import {
  StyledHeader,
  StyledTitle,
  StyledBody,
  StyledBalance,
  StyledBalanceColumn,
  StyledBalanceLabel,
  StyledBalanceValue,
  StyledDividerVertical,
  StyledLabel,
  StyledStepper,
  StyledStepperButton,
  StyledStepperCenter,
  StyledStepperAmount,
  StyledStepperHint,
  StyledOrderSummary,
  StyledOrderRow,
  StyledOrderLabel,
  StyledOrderValue,
  StyledDivider,
  StyledRedeemButton,
  StyledDisclaimer,
  StyledDisclaimerLink,
  StyledTooltip,
  StyledYourfullPointsBalance,
  StyledRewardRedeemedAppliesOnly,
} from './LoyaltyRedeemModal.css'

type LoyaltyRedeemModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  totalPoints: number
  redeemableAmount: number
  orderTotal: number
  onRedeem: ({ selectedAmount }: { selectedAmount: number }) => Promise<void> | void
  pointsPerDollar: number
}

const RedeemModalUnion = () => (
  <div data-svg-wrapper style={{ position: 'absolute', top: 0, left: 0, right: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
    <svg width="100%" viewBox="0 0 343 248" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M233.677 -29.5L219.316 45.1729C212.058 82.9012 237.912 113.483 275.629 113.517L349.125 113.572L342.886 146.002H260.203C252.608 146.002 245.518 144.366 239.323 141.319C238.461 140.894 237.441 141.308 236.825 142.036C236.377 142.563 236.13 143.247 236.332 143.919C237.934 149.329 238.539 155.166 238.068 161.192C237.912 163.209 237.642 165.236 237.25 167.274L221.893 247.078H189.015L203.387 172.394C210.634 134.665 184.78 104.085 147.063 104.051L-104.5 103.994V71.5645H162.488C170.083 71.5645 177.174 73.2001 183.369 76.2471C184.299 76.7061 185.419 76.2019 186.013 75.373C186.36 74.8802 186.539 74.264 186.359 73.6592C184.758 68.2487 184.164 62.4123 184.623 56.3857C184.78 54.3694 185.049 52.3415 185.441 50.3027L200.799 -29.5H233.677ZM144.676 114.548C179.929 114.548 198.77 142.385 192.9 174.468L178.942 247.012H146.815L161.479 170.793H161.49C164.011 157.687 155.43 147.067 142.323 147.067H-107V114.547L144.676 114.548ZM261.442 46.7188H261.432C258.911 59.8249 267.492 70.4441 280.598 70.4443H357.422L351.048 102.964H278.246C242.993 102.964 224.151 75.1265 230.021 43.0439L243.979 -29.5H276.106L261.442 46.7188Z" fill="#59C369" fill-opacity="0.1"/>
    </svg>
  </div>
)

const ToolTipArrowDown = () => (
  <svg width="9" height="6" viewBox="0 0 9 6" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.24315 4.90107C5.47854 6.26037 3.52146 6.26037 2.75685 4.90106L6.99382e-07 -7.86805e-07L9 0L6.24315 4.90107Z" fill="white"/>
  </svg>
)

const INCREMENT = 10

const LoyaltyRedeemModal: React.FC<LoyaltyRedeemModalProps> = ({
  open,
  onOpenChange,
  totalPoints,
  redeemableAmount,
  orderTotal,
  pointsPerDollar,
  onRedeem,
}) => {
  const [selectedAmount, setSelectedAmount] = useState(Number(redeemableAmount) || 0)
  const [showBalanceTooltip, setShowBalanceTooltip] = useState(false)
  const [showRedeemTooltip, setShowRedeemTooltip] = useState(false)
  const [isRedeeming, setIsRedeeming] = useState(false)
  const balanceSpanRef = useRef<HTMLSpanElement>(null)
  const redeemSpanRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!showBalanceTooltip && !showRedeemTooltip) return
    const handleOutsideClick = (e: MouseEvent) => {
      if (showBalanceTooltip && !balanceSpanRef.current?.contains(e.target as Node)) {
        setShowBalanceTooltip(false)
      }
      if (showRedeemTooltip && !redeemSpanRef.current?.contains(e.target as Node)) {
        setShowRedeemTooltip(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [showBalanceTooltip, showRedeemTooltip])

  useEffect(() => {
    if (!open) {
      setSelectedAmount(Number(redeemableAmount) || 0)
      setShowBalanceTooltip(false)
      setShowRedeemTooltip(false)
      setIsRedeeming(false)
    }
  }, [open, redeemableAmount])

  const pointsUsed = (selectedAmount * +pointsPerDollar) / INCREMENT
  const newOrderTotal = orderTotal - selectedAmount

  const canDecrement = selectedAmount > INCREMENT
  const canIncrement = selectedAmount + INCREMENT <= redeemableAmount

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      header={null}
      desktopWidth="480px"
      content={(
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <RedeemModalUnion />
          <StyledHeader>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ borderRadius: '4px', padding: '4px 8px', display: 'inline-flex' }}>
                <CarpartsPlusGoldLogoModal />
              </div>
              <StyledTitle>Redeem Your Reward</StyledTitle>
            </div>
          </StyledHeader>

          <StyledBody>
            <StyledBalance>
              <StyledBalanceColumn>
                <StyledBalanceLabel>
                  Total Balance
                  <span ref={balanceSpanRef} style={{ position: 'relative', display: 'inline-flex' }}>
                    <svg
                      width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"
                      style={{ cursor: 'pointer', flexShrink: 0, display: 'block' }}
                      onClick={() => { setShowRedeemTooltip(false); setShowBalanceTooltip(p => !p) }}
                    >
                      <circle cx="6" cy="6" r="5.5" stroke="#5E5E65"/>
                      <rect x="5.25" y="5" width="1.5" height="4" rx="0.75" fill="#5E5E65"/>
                      <circle cx="6" cy="3.5" r="0.875" fill="#5E5E65"/>
                    </svg>
                    {showBalanceTooltip && (
                      <StyledTooltip style={{ position: 'absolute', bottom: 'calc(100% + 10px)', left: '50%', transform: 'translateX(-50%)', zIndex: 10, width: '220px' }}>
                        <StyledYourfullPointsBalance>
                          Your full points balance. We cap redemption at your subtotal so you don&apos;t lose points. Points must be redeemed in 500-point increments.
                        </StyledYourfullPointsBalance>
                        <div style={{ position: 'absolute', bottom: '-10px', left: '50%', transform: 'translateX(-50%)', filter: 'drop-shadow(0px 2px 2px rgba(0,0,0,0.18))' }}>
                          <ToolTipArrowDown />
                        </div>
                      </StyledTooltip>
                    )}
                  </span>
                </StyledBalanceLabel>
                <StyledBalanceValue>{totalPoints} points</StyledBalanceValue>
              </StyledBalanceColumn>
              <StyledDividerVertical />
              <StyledBalanceColumn>
                <StyledBalanceLabel>
                  Ready to Redeem
                  <span ref={redeemSpanRef} style={{ position: 'relative', display: 'inline-flex' }}>
                    <svg
                      width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"
                      style={{ cursor: 'pointer', flexShrink: 0, display: 'block' }}
                      onClick={() => { setShowBalanceTooltip(false); setShowRedeemTooltip(p => !p) }}
                    >
                      <circle cx="6" cy="6" r="5.5" stroke="#5E5E65"/>
                      <rect x="5.25" y="5" width="1.5" height="4" rx="0.75" fill="#5E5E65"/>
                      <circle cx="6" cy="3.5" r="0.875" fill="#5E5E65"/>
                    </svg>
                    {showRedeemTooltip && (
                      <StyledTooltip style={{ position: 'absolute', bottom: 'calc(100% + 10px)', left: '50%', transform: 'translateX(-50%)', zIndex: 10, width: '220px' }}>
                        <StyledRewardRedeemedAppliesOnly>
                          Reward redeemed applies only to subtotal. Shipping and tax not included.
                        </StyledRewardRedeemedAppliesOnly>
                        <div style={{ position: 'absolute', bottom: '-10px', left: '50%', transform: 'translateX(-50%)', filter: 'drop-shadow(0px 2px 2px rgba(0,0,0,0.18))' }}>
                          <ToolTipArrowDown />
                        </div>
                      </StyledTooltip>
                    )}
                  </span>
                </StyledBalanceLabel>
                <StyledBalanceValue>${selectedAmount}</StyledBalanceValue>
              </StyledBalanceColumn>
            </StyledBalance>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <StyledLabel>Select reward amount to apply to this order.</StyledLabel>
              <StyledStepper>
                <StyledStepperButton
                  disabled={!canDecrement}
                  onClick={() => canDecrement && setSelectedAmount(a => a - INCREMENT)}
                >
                  −
                </StyledStepperButton>
                <StyledStepperCenter>
                  <StyledStepperAmount>${selectedAmount}</StyledStepperAmount>
                  <StyledStepperHint>{`In $${INCREMENT} increments. Using ${pointsUsed} pts.`}</StyledStepperHint>
                </StyledStepperCenter>
                <StyledStepperButton
                  disabled={!canIncrement}
                  onClick={() => canIncrement && setSelectedAmount(a => a + INCREMENT)}
                >
                  +
                </StyledStepperButton>
              </StyledStepper>
            </div>

            <StyledOrderSummary>
              <StyledOrderRow>
                <StyledOrderLabel>Order Total</StyledOrderLabel>
                <StyledOrderValue>${orderTotal.toFixed(2)}</StyledOrderValue>
              </StyledOrderRow>
              <StyledOrderRow>
                <StyledOrderLabel>Coupon</StyledOrderLabel>
                <StyledOrderValue highlight>–${selectedAmount?.toFixed(2)}</StyledOrderValue>
              </StyledOrderRow>
              <StyledDivider />
              <StyledOrderRow>
                <StyledOrderLabel><strong>New Order Total</strong></StyledOrderLabel>
                <StyledOrderValue bold>${newOrderTotal.toFixed(2)}</StyledOrderValue>
              </StyledOrderRow>
            </StyledOrderSummary>

            <StyledRedeemButton
              disabled={isRedeeming}
              onClick={() => {
                if (isRedeeming) return
                setIsRedeeming(true)
                const result = onRedeem({ selectedAmount })
                if (result instanceof Promise) {
                  result.finally(() => setIsRedeeming(false))
                } else {
                  setIsRedeeming(false)
                }
              }}
            >
              {isRedeeming ? 'Redeeming…' : 'Redeem Reward'}
            </StyledRedeemButton>

            <StyledDisclaimer>
              Coupon must be redeemed in a single purchase. No remaining balance will be carried over. Coupons are valid for 90 days. Exclusions apply.{' '}
              <StyledDisclaimerLink href="/blog/eligible-brands/" target="_blank">See list of included brands</StyledDisclaimerLink>. Check{' '}
              <StyledDisclaimerLink href="/help-center/carparts-plus-membership-terms-and-conditions?referer=carparts_plus_membership" target="_blank">terms &amp; conditions</StyledDisclaimerLink> for more information.
            </StyledDisclaimer>

          </StyledBody>
        </div>
      )}
    />
  )
}

export default LoyaltyRedeemModal
